import { CookieOptions } from '@/sections/cookies/CookieConsent.config';
import {
	Dispatch,
	SetStateAction,
	useEffect,
	useSyncExternalStore,
} from 'react';
import { hostEnv } from '../config/helpers/configEnvironment';

const stringifyCookieOptions = (options: CookieOptions) => {
	const parts: string[] = ['SameSite=Strict', 'Path=/'];

	// Otherwise the cookie will not work on localhost
	if (hostEnv === 'server') {
		parts.push('Secure');
	}
	parts.push(`max-age=${options.maxAge}`);
	return parts.join('; ');
};

export const createCookieStore = <Type,>({
	key,
	parse,
	stringify,
	options,
}: {
	key: string;
	parse: (value: string | null) => Type | null;
	stringify: (value: Type | null) => string | null;
	options: CookieOptions;
}) => {
	const listeners = new Set<(value: Type | null) => void>();

	let itemCache: { value: Type | null } = { value: null };

	const clearStorage = () => {
		itemCache = { value: null };
		document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
	};

	const getItem = () => {
		if (itemCache.value != null) {
			return itemCache.value;
		}
		for (const cookie of document.cookie.split('; ')) {
			const [cookieKey, cookieValue] = cookie.split('=');
			if (cookieKey === key) {
				const itemValue = parse(cookieValue);
				itemCache = { value: itemValue };
				return itemValue;
			}
		}

		return null;
	};

	const setItem = (value: Type | null) => {
		const stringified = stringify(value);
		itemCache = { value };

		if (value == null || stringified == null) clearStorage();
		else
			document.cookie = `${key}=${stringified}; ${stringifyCookieOptions(options)}`;
	};

	const getSnapshot = (initialValue: Type): Type => {
		const item = getItem();
		if (item != null) {
			return item;
		}
		return initialValue;
	};

	const set: Dispatch<SetStateAction<Type | null>> = (
		value: (Type | null) | ((oldValue: Type | null) => Type | null),
	) => {
		const valueToStore = value instanceof Function ? value(getItem()) : value;
		setItem(valueToStore);
		for (const listener of listeners) {
			listener(valueToStore);
		}
	};

	const subscribe = (listener: () => void) => {
		listeners.add(listener);

		return () => {
			listeners.delete(listener);
		};
	};

	const renew = () => {
		const item = getItem();
		if (item == null) return;
		setItem(item);
	};

	return {
		getSnapshot,
		set,
		subscribe,
		parse,
		renew,
	};
};

/**
 * Store a string enum in cookie
 */
export const createCookieStringStore = <
	AllowedValues extends ReadonlyArray<string>,
>(
	key: string,
	allowedValues: AllowedValues,
	options: CookieOptions,
) => {
	return createCookieStore({
		key,
		parse: (value) => {
			if (value == null) return null;
			if (allowedValues.includes(value)) return value as AllowedValues[number];
			return null;
		},
		stringify: (value) => value,
		options,
	});
};
export const useCookieStore = <Type,>(
	store: ReturnType<typeof createCookieStore<Type>>,
	initialValue: Type,
	serverValue: string | null,
) => {
	const value = useSyncExternalStore(
		store.subscribe,
		() => store.getSnapshot(initialValue),
		() => store.parse(serverValue) ?? initialValue,
	);
	useEffect(() => {
		store.renew();
	}, [store]);

	return [value, store.set] as const;
};
